import React from 'react'
import type { HotelRow, RoomTypeRow } from '@/lib/types'
import type { ImportIssue, ImportPreview, UnresolvedRef } from '@/lib/excel/importPreview'
import { Select } from '@/components/ui'

interface Props {
  preview: ImportPreview
  hotels: HotelRow[]
  roomTypes: RoomTypeRow[]
  onChangePreview: (next: ImportPreview) => void
}

/**
 * Shared "what will happen if I apply this file" panel (sections 36-38):
 * stats, unresolved hotel/room-type name mapping, and the error/warning
 * lists. Nothing here writes to the database -- it only edits the in-memory
 * ImportPreview until the caller is happy enough to call applyImport().
 */
export default function ImportPreviewPanel({ preview, hotels, roomTypes, onChangePreview }: Props) {
  const errors = preview.issues.filter((i) => i.severity === 'error')
  const warnings = preview.issues.filter((i) => i.severity === 'warning')

  function setResolution(key: string, resolution: UnresolvedRef['resolution']) {
    onChangePreview({
      ...preview,
      unresolved: preview.unresolved.map((u) => (u.key === key ? { ...u, resolution } : u)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Stat label="Companies" value={preview.stats.companies} />
        <Stat label="Hotels" value={preview.stats.hotels} />
        <Stat label="Allocations" value={preview.stats.allocations} />
        <Stat label="Rooms" value={preview.stats.rooms} />
        <Stat label="Guest Capacity" value={preview.stats.guestCapacity} />
        <Stat label="Guests" value={preview.stats.guests} />
      </div>

      {preview.unresolved.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h4 className="mb-1 text-sm font-semibold text-amber-900">Unrecognized hotels / room types</h4>
          <p className="mb-3 text-xs text-amber-800">Map each name to an existing record, or approve creating a new one. Nothing is saved until you resolve all of these.</p>
          <div className="space-y-2">
            {preview.unresolved.map((u) => (
              <div key={u.key} className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-white px-3 py-2">
                <div className="text-sm">
                  <span className="font-medium text-slate-900">{u.originalName}</span>{' '}
                  <span className="text-xs text-slate-500">({u.kind === 'hotel' ? 'Hotel' : 'Room Type'})</span>
                </div>
                <Select
                  className="max-w-xs"
                  value={u.resolution.type === 'existing' ? u.resolution.id : u.resolution.type}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v === 'create') setResolution(u.key, { type: 'create' })
                    else if (v === 'unresolved') setResolution(u.key, { type: 'unresolved' })
                    else setResolution(u.key, { type: 'existing', id: v })
                  }}
                >
                  <option value="unresolved">— Choose what to do —</option>
                  <option value="create">+ Create new {u.kind === 'hotel' ? 'hotel' : 'room type'} "{u.originalName}"</option>
                  {(u.kind === 'hotel' ? hotels : roomTypes).map((r) => (
                    <option key={r.id} value={r.id}>
                      Map to existing: {r.name}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {errors.length > 0 && <IssueList title="Errors — must be fixed before import" tone="red" issues={errors} />}
      {warnings.length > 0 && <IssueList title="Warnings" tone="amber" issues={warnings} />}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-center">
      <div className="text-lg font-semibold text-slate-900">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  )
}

function IssueList({ title, tone, issues }: { title: string; tone: 'red' | 'amber'; issues: ImportIssue[] }) {
  return (
    <div className={`rounded-lg border p-4 ${tone === 'red' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
      <h4 className={`mb-2 text-sm font-semibold ${tone === 'red' ? 'text-red-900' : 'text-amber-900'}`}>
        {title} ({issues.length})
      </h4>
      <ul className={`max-h-48 space-y-1 overflow-y-auto text-xs ${tone === 'red' ? 'text-red-800' : 'text-amber-800'}`}>
        {issues.map((iss, i) => (
          <li key={i}>
            {iss.sheet} row {iss.row}: {iss.message}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function importHasBlockingIssues(preview: ImportPreview): boolean {
  return preview.issues.some((i) => i.severity === 'error') || preview.unresolved.some((u) => u.resolution.type === 'unresolved')
}
